import type { ApiResponse } from 'src/common/interfaces';
import type {
  AnalyticsEvent,
  TrackAnonymousPageViewBody,
  TrackButtonClickBody,
  TrackCTAClickBody,
  TrackFormOpenBody,
  TrackFormSubmitBody,
  TrackPageBody,
} from 'src/common/interfaces/segment';
import { getRedditClickId } from 'src/common/tracking/data';
import { getSessionReplayProperties } from 'src/common/tracking/session-replay';
import { apiSlice } from 'src/features/api/api-slice';

export const analyticsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    trackViewPage: builder.mutation<ApiResponse, TrackPageBody>({
      query: (body) => {
        const sessionReplayProperties = getSessionReplayProperties();
        return {
          method: 'POST',
          url: '/analytics/page-view',
          body: {
            ...body,
            ...sessionReplayProperties,
          },
        };
      },
    }),
    trackAnonymousViewPage: builder.mutation<ApiResponse, TrackAnonymousPageViewBody>({
      query: (body) => {
        const sessionReplayProperties = getSessionReplayProperties();
        return {
          method: 'POST',
          url: '/analytics/anonymous-page-view',
          body: {
            ...body,
            ...sessionReplayProperties,
          },
        };
      },
    }),
    /**
     * @deprecated Use trackCTAClick instead
     */
    trackButtonClick: builder.mutation<ApiResponse, TrackButtonClickBody>({
      query: (body) => ({
        method: 'POST',
        url: '/analytics/button-click',
        body,
      }),
    }),
    trackCTAClick: builder.mutation<ApiResponse, TrackCTAClickBody>({
      query: (body) => {
        const sessionReplayProperties = getSessionReplayProperties();
        return {
          method: 'POST',
          url: '/analytics/cta-click',
          body: {
            ...body,
            ...sessionReplayProperties,
          },
        };
      },
    }),
    trackFormOpen: builder.mutation<ApiResponse, TrackFormOpenBody>({
      query: (body) => {
        const sessionReplayProperties = getSessionReplayProperties();
        return {
          method: 'POST',
          url: '/analytics/form-open',
          body: {
            ...body,
            ...sessionReplayProperties,
          },
        };
      },
    }),
    trackFormSubmit: builder.mutation<ApiResponse, TrackFormSubmitBody>({
      query: (body) => {
        const sessionReplayProperties = getSessionReplayProperties();
        return {
          method: 'POST',
          url: '/analytics/form-submit',
          body: {
            ...body,
            ...sessionReplayProperties,
          },
        };
      },
    }),
    trackEvent: builder.mutation<undefined, AnalyticsEvent>({
      query: (event) => {
        const redditClickId = getRedditClickId();
        const sessionReplayProperties = getSessionReplayProperties();
        const enhancedEvent = {
          ...event,
          properties: {
            ...event.properties,
            ...(redditClickId && { click_id: redditClickId }),
            ...sessionReplayProperties,
          },
        };
        return {
          method: 'POST',
          url: '/client-insights',
          body: enhancedEvent,
        };
      },
    }),
  }),
});

export const {
  useTrackViewPageMutation,
  useTrackAnonymousViewPageMutation,
  useTrackButtonClickMutation,
  useTrackCTAClickMutation,
  useTrackFormOpenMutation,
  useTrackFormSubmitMutation,
  useTrackEventMutation,
  endpoints: analyticsEndpoints,
} = analyticsApiSlice;
